import React, { memo, useCallback, useState } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MIN_SEGMENT_WIDTH } from '../timeUtils';
import TotalVideoSegmentDuration from './TotalVideoSegmentDuration';
import { getSegmentDisplayText, getSegmentDisplayTextWithIntroOutroPrefix, truncateDisplayText } from '../utils';
import VideoSegmentTrimHandles from './VideoSegmentTrimHandles';
import { useVideoSegmentDrag } from '../hooks/useVideoSegmentDrag';
import { useTrimHandleHover } from '../hooks/useTrimHandleHover';
import { useRemoveVideoSegment } from '../hooks/useRemoveVideoSegment';
import { useMenuVisibility, MENU_BREAKPOINTS_WITHOUT_DRAG } from '../hooks/useMenuVisibility';
import { VideoSegment } from '@/App/remotion/MainPlayer/types/Segment/VideoSegment';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

function SplitsIntroOutroSegment({
  segment,
  type,
  videoElementHeight,
  newTimelineContainerRef,
  speakerTimelineContainerRef
}: Readonly<{
  segment: VideoSegment;
  type: 'intro' | 'outro';
  videoElementHeight: number;
  newTimelineContainerRef?: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef?: React.RefObject<HTMLDivElement>;
}>) {
  const { clipId, clipData, fps } = useClipsContext();
  const {
    dragStart: videoSegmentDragStart,
    dragEnd: videoSegmentDragEnd,
    setDragStart: setVideoSegmentDragStart,
    setDragEnd: setVideoSegmentDragEnd,
    segmentWidth
  } = useVideoSegmentDrag(segment, fps);
  const { showTrimHandlesOnHover, segmentRef } = useTrimHandleHover();
  const removeVideoSegment = useRemoveVideoSegment(clipId, segment.id);
  const [areControlsVisible, setAreControlsVisible] = useState(false);
  const menuVisibility = useMenuVisibility(segmentRef, MENU_BREAKPOINTS_WITHOUT_DRAG);

  const { setNodeRef, transform, transition } = useSortable({
    id: segment.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition: transition
  };

  const showTime = menuVisibility === 'all';
  const showTrash = menuVisibility !== 'none';

  const setNodeRefForSegment = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      segmentRef.current = node;
    },
    [setNodeRef, segmentRef]
  );

  return (
    <div
      ref={setNodeRefForSegment}
      className={classnames('group relative', type === 'intro' ? 'pr-[1.5px]' : 'pl-[1.5px]')}
      style={{
        width: `max(calc(${segmentWidth}px * var(--timeline-zoom)),${MIN_SEGMENT_WIDTH}px)`,
        height: videoElementHeight ? `${videoElementHeight}px` : undefined,
        ...style
      }}
      data-testid={segment.id}
      onMouseEnter={() => setAreControlsVisible(true)}
      onMouseLeave={() => setAreControlsVisible(false)}
    >
      {areControlsVisible && menuVisibility !== 'none' && (
        <div className="absolute bottom-[8px] right-[10px] z-50 flex items-center gap-0 rounded-md bg-black px-1.5 py-1 text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover/composite-segment:opacity-100 group-hover:opacity-100">
          {showTime && (
            <div className="mr-1 text-xs text-white">
              <TotalVideoSegmentDuration
                segment={segment}
                showShortTime={true}
                dragStart={videoSegmentDragStart}
                dragEnd={videoSegmentDragEnd}
              />
            </div>
          )}
          {showTrash && (
            <div
              onClick={e => {
                e.stopPropagation();
                removeVideoSegment();
              }}
              className="group/delete flex cursor-pointer items-center justify-center rounded p-1.5 text-white transition-all duration-200 hover:bg-red-500/20 active:bg-red-500/30"
              data-testid="composite-segment-delete-button"
            >
              <IconTrash
                size={12}
                className="text-white transition-colors duration-200 group-hover/delete:text-red-500"
              />
            </div>
          )}
        </div>
      )}
      <div className="group/intro-outro relative flex h-full flex-col gap-1">
        <div
          className={classnames(
            'absolute inset-0 rounded-[7px] border-2 border-slate-200 group-hover/intro-outro:border-slate-800',
            showTrimHandlesOnHover ? 'border-slate-950' : ''
          )}
        ></div>
        <div className="group/chapter relative mx-[4px] mt-[4px] flex min-h-[25px] items-center justify-center overflow-hidden rounded-[3px]">
          <div className="pt-[5px] text-xs font-medium">{segment.id === 'intro' ? 'Intro' : 'Outro'}</div>
        </div>
        <div className="relative m-[4px] flex h-full flex-col overflow-hidden rounded-[3px] bg-slate-100 px-[10px] py-px">
          <div className="truncate pt-2 text-xs font-medium text-slate-500">
            <video
              className="relative top-[3px] aspect-video max-h-[80%] max-w-[100px] rounded-md object-cover"
              src={segment.segment_metadata?.src}
              controls={false}
              autoPlay={false}
              muted={true}
              preload="metadata"
            />
          </div>
          <div
            className="mt-auto truncate pb-2 text-2xs text-slate-600"
            title={getSegmentDisplayTextWithIntroOutroPrefix(segment)}
          >
            <span className="truncate">{truncateDisplayText(getSegmentDisplayText(segment))}</span>
          </div>
        </div>
      </div>
      {newTimelineContainerRef && speakerTimelineContainerRef && (
        <VideoSegmentTrimHandles
          fps={fps}
          clipId={clipId}
          clipData={clipData}
          segment={segment}
          showTrimsBars={showTrimHandlesOnHover}
          videoSegmentDragStart={videoSegmentDragStart || 0}
          videoSegmentDragEnd={videoSegmentDragEnd || 0}
          setVideoSegmentDragStart={setVideoSegmentDragStart}
          setVideoSegmentDragEnd={setVideoSegmentDragEnd}
          newTimelineContainerRef={newTimelineContainerRef}
          speakerTimelineContainerRef={speakerTimelineContainerRef}
        />
      )}
    </div>
  );
}

export default memo(SplitsIntroOutroSegment);
